import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";

const useIsomorphicLayoutEffect =
	typeof window !== "undefined" ? useLayoutEffect : useEffect;

export const useElementSizeByRef = (ref: any) => {
	const [size, setSize] = useState(getRect());
	const handleResize = useCallback(() => {
		if (!ref.current) {
			return;
		}
		setSize(getRect(ref.current));
	}, [ref]);

	const getSize = (ref: any) => {
		const _size = getRect(ref.current);
		setSize(_size);
		return _size;
	};

	useIsomorphicLayoutEffect(() => {
		const element = ref.current;
		if (!element) {
			return;
		}

		handleResize();

		if (typeof ResizeObserver === "function") {
			let resizeObserver = new ResizeObserver(() => handleResize());
			resizeObserver.observe(element);

			return () => {
				if (!resizeObserver) {
					return;
				}

				resizeObserver.disconnect();
				resizeObserver = null as any;
			};
		} else {
			// Browser support, remove freely
			window.addEventListener("resize", handleResize);

			return () => {
				window.removeEventListener("resize", handleResize);
			};
		}
	}, [ref, handleResize]);

	return {
		ref,
		size,
		getSize,
	};
};

const useElementSize = () => {
	const ref = useRef(null) as any;
	return useElementSizeByRef(ref);
};

function getRect(element?: any) {
	if (!element) {
		return {
			bottom: 0,
			height: 0,
			left: 0,
			right: 0,
			top: 0,
			width: 0,
		};
	}

	return element.getBoundingClientRect();
}

export default useElementSize;
